import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Wordmark from '../components/Brand';

// ── ① Navbar
function Navbar() {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo-mark.svg" alt="MaioS" className="h-7 w-7" />
          <Wordmark className="text-xl" />
          <span className="hidden sm:inline text-xs text-gray-400 font-medium">My All-In-One Solution</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors">
            로그인
          </Link>
          <button
            onClick={() => navigate('/register')}
            className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            무료 베타 시작하기
          </button>
        </div>
      </div>
    </header>
  );
}

// ── ① Hero
function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="bg-gradient-to-b from-primary-50 to-white pt-20 pb-24 px-4 text-center">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
          요구사항부터 테스트까지,
          <br />
          끊김 없이 이어지는 하나의 흐름
        </h1>
        <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Jira로 태스크 관리하고, Confluence로 문서 쓰고, TestRail로 테스트 관리하고...
          <br className="hidden sm:inline" />
          3개 도구를 오가는 데 인당 월 7만원. MaioS는 이걸 하나로 묶었습니다.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate('/register')}
            className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3 rounded-xl text-base transition-colors shadow-sm"
          >
            무료 베타 시작하기
          </button>
          <a
            href="#features"
            className="w-full sm:w-auto border border-gray-300 hover:border-primary-400 text-gray-700 hover:text-primary-600 font-medium px-8 py-3 rounded-xl text-base transition-colors text-center"
          >
            어떻게 작동하나요 →
          </a>
        </div>
        <p className="mt-4 text-sm text-gray-400">신용카드 없이 시작 · 베타 기간 전 기능 무료</p>

        {/* 스크린샷 영역 — 권장 사이즈: 1200 × 720px */}
        <div className="mt-14 bg-gray-100 border-2 border-dashed border-gray-300 rounded-2xl h-72 sm:h-96 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <p className="text-sm font-medium">스크린샷 영역</p>
            <p className="text-xs mt-1">권장 사이즈: 1200 × 720px</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── ② Problem
const PAIN_POINTS = [
  {
    quote: '"이 태스크, 대체 어떤 요구사항에서 나온 거지?"',
    desc: '요구사항은 Confluence에, 태스크는 Jira에, 테스트는 TestRail에. 연결고리를 찾으려면 탭 3개를 동시에 띄워야 합니다.',
  },
  {
    quote: '"PR은 머지됐는데 Jira 상태는 그대로네."',
    desc: '코드는 GitHub에서 끝났는데, 태스크 상태는 누군가 수동으로 바꿔줘야 합니다. 까먹으면? 현황판은 거짓말을 합니다.',
  },
  {
    quote: '"이 기능, 테스트는 다 한 거 맞아?"',
    desc: '요구사항 하나가 어떤 테스트 케이스로 검증됐는지 추적하려면, 결국 사람의 기억에 의존하게 됩니다.',
  },
];

function ProblemSection() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900">혹시, 이런 적 없으셨나요?</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PAIN_POINTS.map((card, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <p className="text-base font-semibold text-gray-800 leading-snug">{card.quote}</p>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-lg font-medium text-gray-500">
          도구가 많아질수록, 진실은 흩어집니다.
        </p>
      </div>
    </section>
  );
}

// ── ③ Features
const FEATURES = [
  {
    label: '추적성 체인 (Traceability Chain)',
    title: '요구사항 하나가, 끝까지 따라옵니다',
    desc: '요구사항 → 기능 명세 → 디자인 → 태스크 → 코드(PR) → 테스트 케이스. MaioS에선 이 모든 단계가 하나의 사슬로 연결됩니다. 클릭 한 번으로 "이 기능이 어디서 왔고, 어떻게 검증됐는지"를 한눈에 봅니다.',
    // 스크린샷/GIF: 추적성 체인 시각화 화면 — 권장 사이즈: 800 × 500px
  },
  {
    label: 'GitHub 연동 (GitHub Sync)',
    title: 'PR이 머지되면, 태스크도 압니다',
    desc: 'GitHub와 실시간 동기화. PR이 열리고, 리뷰되고, 머지되는 순간 태스크 상태가 자동으로 따라갑니다. 더 이상 수동으로 상태를 바꾸지 않아도, 현황판은 항상 진실을 말합니다.',
    // 스크린샷/GIF: PR 상태가 태스크에 자동 반영되는 장면 — 권장 사이즈: 800 × 500px
  },
  {
    label: '테스트 관리 (Test Management)',
    title: 'QA 엔지니어가 직접 설계한 테스트 관리',
    desc: '테스트 케이스를 요구사항에 직접 연결하세요. 어떤 요구사항이 검증됐고, 어떤 게 아직 남았는지 명확하게 보입니다. 12년차 QA 엔지니어가 "내가 쓰고 싶은 도구"를 기준으로 만들었습니다.',
    // 스크린샷/GIF: 테스트 케이스-요구사항 연결 화면 — 권장 사이즈: 800 × 500px
  },
];

function FeaturesSection() {
  return (
    <section id="features" className="py-20 px-4 bg-white">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900">
          MaioS는 흩어진 점들을 하나의 선으로 잇습니다
        </h2>
        <div className="mt-14 flex flex-col gap-20">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-10`}
            >
              <div className="flex-1">
                <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">{f.label}</span>
                <h3 className="mt-2 text-2xl font-bold text-gray-900">{f.title}</h3>
                <p className="mt-3 text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
              {/* 스크린샷 영역 — 권장 사이즈: 800 × 500px */}
              <div className="flex-1 w-full bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl h-56 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <p className="text-sm font-medium">스크린샷 영역</p>
                  <p className="text-xs mt-1">권장 사이즈: 800 × 500px</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── ④ Comparison
const COMPARISON_ROWS = [
  { label: '요구사항→테스트 추적성', maios: '✅ 기본 제공', jira: '⚠️ 도구 3개 조합 필요', clickup: '⚠️ 제한적' },
  { label: 'GitHub PR 자동 동기화', maios: '✅', jira: '⚠️ 플러그인·설정 필요', clickup: '✅' },
  { label: '테스트 케이스 관리', maios: '✅ 내장', jira: '❌ TestRail 별도', clickup: '❌' },
  { label: '실시간 문서 협업', maios: '✅', jira: '❌ Confluence 별도', clickup: '✅' },
  { label: '실시간 채팅', maios: '✅', jira: '⚠️ 별도 연동', clickup: '✅' },
];

function ComparisonSection() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900">왜 MaioS인가요?</h2>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
          <table className="w-full min-w-[560px] bg-white text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-5 py-4 font-semibold text-gray-600 w-2/5">항목</th>
                <th className="text-center px-4 py-4 font-bold text-primary-700 bg-primary-50">MaioS</th>
                <th className="text-center px-4 py-4 font-semibold text-gray-600">Jira + 별도 도구</th>
                <th className="text-center px-4 py-4 font-semibold text-gray-600">ClickUp</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={i} className={i < COMPARISON_ROWS.length - 1 ? 'border-b border-gray-100' : ''}>
                  <td className="px-5 py-4 text-gray-700 font-medium">{row.label}</td>
                  <td className="px-4 py-4 text-center bg-primary-50 text-gray-800">{row.maios}</td>
                  <td className="px-4 py-4 text-center text-gray-600">{row.jira}</td>
                  <td className="px-4 py-4 text-center text-gray-600">{row.clickup}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-center text-gray-400">
          비교는 일반적인 기능 구성 기준이며, 각 도구의 플랜에 따라 다를 수 있습니다.
        </p>
      </div>
    </section>
  );
}

// ── ⑤ Beta
function BetaSection() {
  const navigate = useNavigate();
  return (
    <section className="py-20 px-4 bg-primary-600">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-white">지금은 무료 베타 기간입니다</h2>
        <p className="mt-5 text-primary-100 leading-relaxed">
          MaioS는 2026년 9월 30일까지 모든 기능을 무료로 제공합니다.
          <br />
          베타 기간에 함께해주신 분들께는, 정식 출시 후 Pro 플랜 할인 혜택을 드릴 예정입니다.
        </p>
        <p className="mt-3 text-primary-100">
          지금 가입하시면, 흩어진 도구들을 하나로 모으는 경험을 가장 먼저 해보실 수 있습니다.
        </p>
        <button
          onClick={() => navigate('/register')}
          className="mt-8 bg-white hover:bg-gray-50 text-primary-600 font-bold px-8 py-3 rounded-xl text-base transition-colors shadow-sm"
        >
          무료로 시작하기
        </button>
        <p className="mt-4 text-sm text-primary-200">
          초기 사용자분들의 피드백 하나하나가 MaioS의 방향이 됩니다.
        </p>
      </div>
    </section>
  );
}

// ── ⑥ FAQ
const FAQS = [
  {
    q: '베타 기간이 끝나면 어떻게 되나요?',
    a: '2026년 9월 30일 이후 Free 플랜으로 자동 전환됩니다. 데이터는 그대로 유지되며, 베타 참여자분께는 Pro 플랜 할인을 안내드립니다.',
  },
  {
    q: '제 데이터는 안전한가요?',
    a: '모든 데이터는 한국(서울) 리전 서버에 저장되며, 조직별로 완전히 분리되어 다른 조직에서 접근할 수 없습니다. 매일 자동 백업도 진행됩니다.',
  },
  {
    q: '기존에 쓰던 Jira/Confluence 데이터를 옮길 수 있나요?',
    a: '현재 베타 단계에서는 데이터 마이그레이션 도구를 제공하지 않습니다. 정식 출시 로드맵에 포함되어 있으며, 필요하신 분은 문의 주시면 개별 안내드립니다.',
  },
  {
    q: '팀원 몇 명까지 무료로 쓸 수 있나요?',
    a: '베타 기간에는 인원 제한 없이 모든 기능을 무료로 사용하실 수 있습니다.',
  },
  {
    q: 'GitHub 말고 다른 연동도 되나요?',
    a: '현재 GitHub 연동이 완성되어 있고, Figma 연동을 준비 중입니다. 더 많은 통합을 점진적으로 추가할 예정입니다.',
  },
];

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900">자주 묻는 질문</h2>
        <div className="mt-10 divide-y divide-gray-100 border border-gray-200 rounded-2xl overflow-hidden">
          {FAQS.map((faq, i) => (
            <div key={i}>
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
              >
                <span className="font-medium text-gray-900">{faq.q}</span>
                {openIdx === i ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                )}
              </button>
              {openIdx === i && (
                <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── ⑦ Footer
function FooterSection() {
  return (
    <footer className="bg-gray-900 text-gray-400 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-2">
              <Wordmark className="text-lg" variant="light" />
              <span className="text-xs text-gray-500">My All-In-One Solution</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
            <div>
              <p className="text-white font-semibold mb-3">서비스</p>
              <ul className="space-y-2">
                <li>
                  <a href="#features" className="hover:text-white transition-colors">
                    기능 소개
                  </a>
                </li>
                <li>
                  <Link to="/register" className="hover:text-white transition-colors">
                    무료 베타 시작하기
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold mb-3">회사 / 정책</p>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    이용약관
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    개인정보처리방침
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold mb-3">문의</p>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:info@maios.kr" className="hover:text-white transition-colors">
                    info@maios.kr
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-gray-800 text-xs text-center">
          © 2026 MaioS. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen font-sans">
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <FeaturesSection />
      <ComparisonSection />
      <BetaSection />
      <FAQSection />
      <FooterSection />
    </div>
  );
}
